import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleCOMPASS } from "@/components/compass/ModuleTitleCOMPASS";

export default function CompassPOQueue() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState({ entries: [], plans_awaiting_po: [] });
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // entry being decided
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compass/entry-requests/queue");
      setQueue(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const decide = async (action) => {
    if (action === "deny" && !notes.trim()) {
      toast.error("Please add notes explaining the denial.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/compass/entry-requests/${decision.id}/po-decision`, {
        action, po_notes: notes,
      });
      toast.success(action === "approve" ? "Approved. Manager may build the plan." : "Denied with notes.");
      setDecision(null);
      setNotes("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="compass-po-queue">
      <Link to="/compass" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> COMPASS home
      </Link>
      <div>
        <ModuleTitleCOMPASS />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">Review Queue</h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          Concur on entry requests, then approve plans awaiting review. Everything is
          audit-logged.
        </p>
      </div>

      <section>
        <h2 className="text-2xl font-serif text-forest-900 mb-3">Entry Requests · {queue.entries.length}</h2>
        {loading && <p className="italic text-charcoal-muted">Loading…</p>}
        {!loading && queue.entries.length === 0 && (
          <div className="patria-card p-8 text-center italic text-charcoal-muted">The entry queue is clear.</div>
        )}
        <ul className="space-y-3">
          {queue.entries.map((e) => (
            <li key={e.id} className="patria-card p-5" data-testid={`entry-row-${e.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-forest-900">
                    {e.employee_name} <span className="text-xs text-charcoal-muted ml-2">({e.gap_category?.replace("_", " ")})</span>
                  </p>
                  <p className="text-xs text-charcoal-muted mt-0.5">
                    From {e.manager_name} · {e.created_at}
                  </p>
                  <p className="mt-3 text-sm text-charcoal-soft whitespace-pre-wrap">{e.gap_description}</p>
                  <p className="mt-3 text-xs uppercase tracking-widest text-charcoal-muted">Prior Feedback</p>
                  <p className="text-sm text-charcoal-soft whitespace-pre-wrap">{e.prior_feedback_notes}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    className="patria-btn-primary"
                    onClick={() => { setDecision({ ...e, action: "approve" }); setNotes(""); }}
                    data-testid={`entry-approve-${e.id}`}
                  >
                    <Check className="h-4 w-4" strokeWidth={1.5} /> Approve
                  </button>
                  <button
                    className="patria-btn-terracotta"
                    onClick={() => { setDecision({ ...e, action: "deny" }); setNotes(""); }}
                    data-testid={`entry-deny-${e.id}`}
                  >
                    <X className="h-4 w-4" strokeWidth={1.5} /> Deny
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-serif text-forest-900 mb-3">
          Plans Awaiting PO Review · {queue.plans_awaiting_po.length}
        </h2>
        {queue.plans_awaiting_po.length === 0 && (
          <div className="patria-card p-8 text-center italic text-charcoal-muted">No plans awaiting review.</div>
        )}
        <ul className="space-y-3">
          {queue.plans_awaiting_po.map((p) => (
            <li key={p.id} className="patria-card p-5 flex items-center gap-4" data-testid={`plan-await-${p.id}`}>
              <div className="flex-1">
                <p className="font-medium text-forest-900">{p.employee_name}</p>
                <p className="text-xs text-charcoal-muted">{p.gap_category?.replace("_", " ")} · {p.timeline_days} days</p>
              </div>
              <button className="patria-btn-primary" onClick={() => navigate(`/compass/plans/${p.id}`)}>
                Review plan
              </button>
            </li>
          ))}
        </ul>
      </section>

      {decision && (
        <div className="fixed inset-0 z-50 bg-forest-900/50 flex items-center justify-center p-4" data-testid="decision-modal">
          <div className="patria-card w-full max-w-lg p-6">
            <h3 className="text-2xl font-serif text-forest-900">
              {decision.action === "approve" ? "Approve entry" : "Deny entry"}
            </h3>
            <p className="mt-2 text-sm text-charcoal-soft">
              For <strong>{decision.employee_name}</strong> · from {decision.manager_name}
            </p>
            <label className="block mt-4 text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
              Notes {decision.action === "deny" && <span className="text-terracotta-500">*</span>}
            </label>
            <textarea
              className="patria-input w-full min-h-[110px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={decision.action === "approve" ? "Optional notes" : "Explain the denial"}
              data-testid="decision-notes"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button className="patria-btn-secondary" onClick={() => setDecision(null)}>Cancel</button>
              <button
                className={decision.action === "approve" ? "patria-btn-primary" : "patria-btn-terracotta"}
                onClick={() => decide(decision.action)}
                disabled={busy}
                data-testid="decision-confirm"
              >
                {busy ? "Working…" : decision.action === "approve" ? "Approve entry" : "Deny with notes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
